import { supabase } from '@/shared/lib/supabase';

/**
 * Admin-side invoice operations.
 *
 * Reads — direct Supabase query of `invoice` + `invoice_line` (RLS lets staff
 * see all rows; customers see their own). Returns rich invoice rows with
 * lifecycle_state and per-customer-link / per-domain scoping (AD-15).
 *
 * Mutations — `resend()` calls the `invoice-resend` Edge Function via
 * `supabase.functions.invoke` carrying an Idempotency-Key header. Other
 * mutations (cancel, write-off, manual mark-paid) are deferred to later slices.
 */

/**
 * Mirrors the live `invoice_lifecycle_state_check` CHECK constraint — all 8
 * states the database permits. `pending_payment` was missing until 2026-07-27,
 * which meant quotations (restricted by `quotation_state_check` to
 * draft/pending_payment/cancelled) were invisible to every consumer that keyed
 * off this type, and an `as` cast hid the gap from the compiler.
 */
export type InvoiceLifecycleState =
  | 'draft'
  | 'pending_payment'
  | 'unpaid'
  | 'grace'
  | 'paid'
  | 'suspended'
  | 'refunded'
  | 'cancelled';

export interface AdminInvoiceLine {
  id: string;
  invoice_id: string;
  description: string;
  target_kind: string;
  target_ref: string | null;
  quantity: number;
  unit_price: number;
  /** > 0 with total === 0 means the item is included free (grant_reason says why). */
  discount_amount: number;
  grant_reason: string | null;
  vat_rate: number;
  vat_amount: number;
  total: number;
  service_plan_id: string | null;
  period_start: string | null;
  period_end: string | null;
  display_order: number;
}

export interface AdminInvoiceRow {
  id: string;
  number: string;
  customer_id: string;
  issue_date: string;
  due_date: string;
  /** Legacy column kept in sync for the duration of the current release. */
  status: string;
  /** AD-13 prepaid lifecycle. Use this in UI; ignore `status`. */
  lifecycle_state: InvoiceLifecycleState | null;
  subtotal: number;
  vat_amount: number;
  total: number;
  currency: 'ZAR';
  paid_at: string | null;
  cancelled_at: string | null;
  grace_until: string | null;
  suspended_at: string | null;
  period_start: string | null;
  period_end: string | null;
  notes: string | null;
  pdf_storage_path: string | null;
  created_at: string;
  /** QM (2026-07-17): 'quotation' = QUO- document awaiting acceptance-by-payment. */
  doc_type: 'quotation' | 'invoice';
  /** Original QUO- number preserved after in-place conversion; null if never quoted. */
  quote_number: string | null;
  quote_expires_at: string | null;
  converted_at: string | null;
  /** ST-2 (2026-07-17): studio_deposit breakdown etc. (cents). */
  quote_metadata: Record<string, number | string> | null;
  /** Bearer token for the public /pay/:id route. Never render it as text. */
  payment_token: string | null;
  /** hosting_renewal | plan_change | service_order | domain_* | studio_installment */
  invoice_kind: string | null;
}

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

interface InvoiceRow {
  id: string;
  number: string;
  customer_id: string;
  issue_date: string;
  due_date: string;
  status: string;
  lifecycle_state: InvoiceLifecycleState | null;
  subtotal: number | string;
  vat_amount: number | string;
  total: number | string;
  currency: 'ZAR';
  paid_at: string | null;
  cancelled_at: string | null;
  grace_until: string | null;
  suspended_at: string | null;
  period_start: string | null;
  period_end: string | null;
  notes: string | null;
  pdf_storage_path: string | null;
  created_at: string;
  doc_type: 'quotation' | 'invoice';
  quote_number: string | null;
  quote_expires_at: string | null;
  converted_at: string | null;
  quote_metadata: Record<string, number | string> | null;
  payment_token: string | null;
  invoice_kind: string | null;
}

interface LineRow {
  id: string;
  invoice_id: string;
  description: string;
  target_kind: string;
  target_ref: string | null;
  quantity: number | string;
  unit_price: number | string;
  discount_amount: number | string | null;
  grant_reason: string | null;
  vat_rate: number | string;
  vat_amount: number | string;
  total: number | string;
  service_plan_id: string | null;
  period_start: string | null;
  period_end: string | null;
  display_order: number;
}

const num = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  return Number(v) || 0;
};

// payment_token + invoice_kind added 2026-07-27 for the admin/support
// copy-pay-link surface. The token is a bearer credential for THIS invoice
// only; PayInvoicePage renders no invoice detail, so a forwarded link exposes
// nothing, and payfast-public-initiate 409s once the invoice is settled.
// Reachable here only because invoice_staff_all RLS already grants staff full
// row access - this select adds no privilege.
const INVOICE_COLS =
  'id, number, customer_id, issue_date, due_date, status, lifecycle_state, subtotal, vat_amount, total, currency, paid_at, cancelled_at, grace_until, suspended_at, period_start, period_end, notes, pdf_storage_path, created_at, doc_type, quote_number, quote_expires_at, converted_at, quote_metadata, payment_token, invoice_kind';

const LINE_COLS =
  'id, invoice_id, description, target_kind, target_ref, quantity, unit_price, discount_amount, grant_reason, vat_rate, vat_amount, total, service_plan_id, period_start, period_end, display_order';

async function invokeEdgeFunction<TRes>(
  name: string,
  body: Record<string, unknown>
): Promise<TRes> {
  const idempotencyKey = crypto.randomUUID();
  const { data, error } = await supabase.functions.invoke<TRes | ApiError>(name, {
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  if (error) {
    const ctx = (error as unknown as { context?: { body?: string } }).context;
    if (ctx?.body) {
      try {
        const parsed = JSON.parse(ctx.body) as ApiError;
        throw new Error(parsed.message ?? error.message);
      } catch {
        // fall through
      }
    }
    throw new Error(error.message);
  }
  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    const apiErr = data as ApiError;
    throw new Error(apiErr.message);
  }
  return data as TRes;
}

const mapInvoice = (row: InvoiceRow): AdminInvoiceRow => ({
  id: row.id,
  number: row.number,
  customer_id: row.customer_id,
  issue_date: row.issue_date,
  due_date: row.due_date,
  status: row.status,
  lifecycle_state: row.lifecycle_state,
  subtotal: num(row.subtotal),
  vat_amount: num(row.vat_amount),
  total: num(row.total),
  currency: row.currency,
  paid_at: row.paid_at,
  cancelled_at: row.cancelled_at,
  grace_until: row.grace_until,
  suspended_at: row.suspended_at,
  period_start: row.period_start,
  period_end: row.period_end,
  notes: row.notes,
  pdf_storage_path: row.pdf_storage_path,
  created_at: row.created_at,
  doc_type: row.doc_type ?? 'invoice',
  quote_number: row.quote_number ?? null,
  quote_expires_at: row.quote_expires_at ?? null,
  converted_at: row.converted_at ?? null,
  quote_metadata: row.quote_metadata ?? null,
  payment_token: row.payment_token ?? null,
  invoice_kind: row.invoice_kind ?? null,
});

const mapLine = (row: LineRow): AdminInvoiceLine => ({
  id: row.id,
  invoice_id: row.invoice_id,
  description: row.description,
  target_kind: row.target_kind,
  target_ref: row.target_ref,
  quantity: num(row.quantity),
  unit_price: num(row.unit_price),
  discount_amount: num(row.discount_amount),
  grant_reason: row.grant_reason ?? null,
  vat_rate: num(row.vat_rate),
  vat_amount: num(row.vat_amount),
  total: num(row.total),
  service_plan_id: row.service_plan_id,
  period_start: row.period_start,
  period_end: row.period_end,
  display_order: row.display_order,
});

export const adminInvoiceService = {
  /**
   * Invoice ids for this customer where the &autopay=1 link would succeed.
   *
   * Reads v_admin_invoices_all rather than recomputing, so the rule lives in
   * exactly one place: public.invoice_autopay_eligible(), which mirrors every
   * 409 in hosting-subscription-public-initiate. Re-deriving it here from
   * invoice_kind alone would offer the link to customers who already hold a
   * live PayFast mandate and enrol them a second time.
   */
  async listAutopayEligibleIds(customerId: string): Promise<Set<string>> {
    const { data, error } = await supabase
      .from('v_admin_invoices_all')
      .select('id, autopay_eligible')
      .eq('customer_id', customerId)
      .eq('autopay_eligible', true);
    // Non-fatal: the billing tab still works, it just omits the auto-pay
    // option. Failing closed is correct for a payment-mandate affordance.
    if (error || !data) return new Set();
    return new Set((data as Array<{ id: string }>).map((r) => r.id));
  },

  async listForCustomer(customerId: string): Promise<AdminInvoiceRow[]> {
    const { data, error } = await supabase
      .from('invoice')
      .select(INVOICE_COLS)
      .eq('customer_id', customerId)
      .order('issue_date', { ascending: false });
    if (error) {
      throw new Error(`Could not load invoices: ${error.message}`);
    }
    return ((data ?? []) as InvoiceRow[]).map(mapInvoice);
  },

  async getLines(invoiceId: string): Promise<AdminInvoiceLine[]> {
    const { data, error } = await supabase
      .from('invoice_line')
      .select(LINE_COLS)
      .eq('invoice_id', invoiceId)
      .order('display_order', { ascending: true });
    if (error) {
      throw new Error(`Could not load invoice lines: ${error.message}`);
    }
    return ((data ?? []) as LineRow[]).map(mapLine);
  },

  async listPaymentsForCustomer(customerId: string): Promise<AdminPaymentRow[]> {
    const { data, error } = await supabase
      .from('payment')
      .select(
        'id, invoice_id, customer_id, gateway, amount, gateway_fee, status, gateway_transaction_id, paid_at, created_at'
      )
      .eq('customer_id', customerId)
      .order('paid_at', { ascending: false, nullsFirst: false });
    if (error) {
      throw new Error(`Could not load payments: ${error.message}`);
    }
    return ((data ?? []) as PaymentRow[]).map((p) => ({
      id: p.id,
      invoice_id: p.invoice_id,
      customer_id: p.customer_id,
      gateway: p.gateway,
      amount: num(p.amount),
      gateway_fee: num(p.gateway_fee),
      status: p.status,
      gateway_transaction_id: p.gateway_transaction_id,
      paid_at: p.paid_at,
      created_at: p.created_at,
    }));
  },

  async listPendingProofsForCustomer(
    customerId: string
  ): Promise<AdminProofOfPayment[]> {
    const { data, error } = await supabase
      .from('proof_of_payment')
      .select(
        'id, customer_id, invoice_id, storage_path, mime_type, file_size_bytes, uploaded_at, status, staff_note'
      )
      .eq('customer_id', customerId)
      .eq('status', 'pending')
      .order('uploaded_at', { ascending: false });
    if (error) {
      throw new Error(`Could not load proofs of payment: ${error.message}`);
    }
    return (data ?? []) as AdminProofOfPayment[];
  },

  async resend(invoiceId: string): Promise<{ ok: true; email_log_id?: string }> {
    return invokeEdgeFunction('invoice-resend', { invoice_id: invoiceId });
  },

  async verifyProofOfPayment(
    proofId: string,
    decision: 'verify' | 'reject',
    staffNote?: string
  ): Promise<{ ok: true }> {
    return invokeEdgeFunction('proof-of-payment-verify', {
      proof_id: proofId,
      decision,
      staff_note: staffNote,
    });
  },
};

export interface AdminPaymentRow {
  id: string;
  invoice_id: string | null;
  customer_id: string;
  gateway: string;
  amount: number;
  gateway_fee: number;
  status: string;
  gateway_transaction_id: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface AdminProofOfPayment {
  id: string;
  customer_id: string;
  invoice_id: string;
  storage_path: string;
  mime_type: string | null;
  file_size_bytes: number | null;
  uploaded_at: string;
  status: 'pending' | 'verified' | 'rejected';
  staff_note: string | null;
}

interface PaymentRow {
  id: string;
  invoice_id: string | null;
  customer_id: string;
  gateway: string;
  amount: number | string;
  gateway_fee: number | string;
  status: string;
  gateway_transaction_id: string | null;
  paid_at: string | null;
  created_at: string;
}
